import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { map } from "lodash";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import ScoreBars, { ScoreStrength } from "@/components/controls/ScoreBars";
import AutofillIndication from "@/components/controls/AutofillIndication";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Close } from "@dreamhub/designsystem/icons";
import { IcpFit } from "@/entities/icpFit/types";
import { getIcpFitTKey, icpFitToScoreBarsMap } from "@/entities/icpFit/helpers";
import { getSafeBullets } from "@/utils/bullets";
import { cn } from "@/utils/utils";
import BulletIcon, {
  IcpFitDetailBullet
} from "@/components/controls/DetailedView/ICPFit/BulletIcon";

interface Props {
  icpFit: number | null | undefined;
  bullets?: IcpFitDetailBullet[];
  previewLabel?: string;
  className?: string;
}

const IcpFitDetailsPopover: React.FC<Props> = ({
  icpFit,
  bullets,
  previewLabel,
  className
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const icpFitValue = icpFit ?? IcpFit.UNKNOWN;
  const icpFitLabel = t(getIcpFitTKey(icpFitValue));
  const safeBullets = getSafeBullets(bullets);

  if (icpFitValue === IcpFit.UNKNOWN) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="link"
          size="default"
          className={cn("h-auto px-2", className)}
        >
          {t("details")}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        padding={0}
        className="w-[360px]"
      >
        <div className="flex items-center gap-4 pl-4 pr-2 py-2">
          <Text variant="lgLabel" className="flex-1">
            {t("details")}
          </Text>
          <AutofillIndication />
          <IconButton
            variant="iconNoBg"
            iconColor="secondary"
            size="md"
            onClick={() => setIsOpen(false)}
            aria-label={t("close")}
          >
            <Close />
          </IconButton>
        </div>
        <div className="h-px w-full bg-border-border1" />
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1">
            <Text variant="bodySecondary" className="px-1">
              {previewLabel ?? t("people.kpis.personIcpFit")}
            </Text>
            <div className="flex items-center gap-2 p-2">
              <ScoreBars
                score={icpFitToScoreBarsMap[icpFitValue] as ScoreStrength}
              />
              <Text variant="body">{icpFitLabel}</Text>
            </div>
          </div>
          {safeBullets.length > 0 && (
            <div className="flex flex-col gap-2 pb-2">
              <Text variant="bodySecondary">
                {t("icpFitDetails.descriptionHeader")}
              </Text>
              <div className="flex flex-col gap-1">
                {map(safeBullets, (bullet, idx) => (
                  <div
                    key={`${bullet.primary}-${idx}`}
                    className="flex items-start gap-2"
                  >
                    <BulletIcon status={bullet.status} />
                    <div className="flex flex-col">
                      <Text variant="body" className="flex-1">
                        {bullet.primary}
                      </Text>
                      {bullet.secondary && (
                        <Text as="span" variant="bodySecondary">
                          {bullet.secondary}
                        </Text>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default IcpFitDetailsPopover;
